import React, { useState } from 'react';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { ProposedTask } from '../../utils/routineHelpers';
import styles from './CreateRoutineModal.module.scss';

interface SortableTaskItemProps {
  task: ProposedTask & { id: string };
  index: number;
  startTime?: string;
  endTime?: string;
  routineColor: string;
  onDurationChange: (index: number, change: number) => void;
  onDelete: (index: number) => void;
  onEdit: (index: number, updates: Partial<ProposedTask>) => void;
  availableIcons: string[];
  availableColors: string[];
}

const formatTimeDisplay = (time: string): string => {
  const [hours, minutes] = time.split(':').map(Number);
  const period = hours >= 12 ? 'PM' : 'AM';
  const displayHours = hours % 12 || 12;
  return `${displayHours}:${minutes.toString().padStart(2, '0')} ${period}`;
};

export const SortableTaskItem: React.FC<SortableTaskItemProps> = ({
  task,
  index,
  startTime,
  endTime,
  routineColor,
  onDurationChange,
  onDelete,
  onEdit,
  availableIcons,
  availableColors,
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const [showIconPicker, setShowIconPicker] = useState(false);
  const [editName, setEditName] = useState(task.label);

  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: task.id,
  });

  const style: React.CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    zIndex: isDragging ? 100 : 'auto',
  };

  const taskColor = task.color || routineColor;

  const handleNameSave = () => {
    if (editName.trim() && editName !== task.label) {
      onEdit(index, { label: editName.trim() });
    }
    setIsEditing(false);
  };

  const handleIconSelect = (icon: string) => {
    onEdit(index, { icon });
    setShowIconPicker(false);
  };

  const handleColorSelect = (color: string) => {
    onEdit(index, { color });
  };

  return (
    <div ref={setNodeRef} style={style} className={styles.taskItem}>
      <div className={styles.dragHandle} {...attributes} {...listeners}>
        ⋮⋮
      </div>

      <div
        className={styles.taskIcon}
        style={{ background: taskColor + '20', color: taskColor, cursor: 'pointer' }}
        onClick={() => setShowIconPicker(!showIconPicker)}
        title="Click to change icon"
      >
        {task.icon}
      </div>

      {showIconPicker && (
        <div className={styles.inlinePickerDropdown}>
          <div className={styles.pickerHeader}>
            <span>Choose Icon</span>
            <button type="button" onClick={() => setShowIconPicker(false)}>
              ×
            </button>
          </div>
          <div className={styles.inlineIconGrid}>
            {availableIcons.map(icon => (
              <button
                key={icon}
                type="button"
                className={`${styles.inlineIconBtn} ${task.icon === icon ? styles.selected : ''}`}
                onClick={() => handleIconSelect(icon)}
              >
                {icon}
              </button>
            ))}
          </div>
        </div>
      )}

      <div className={styles.taskDetails}>
        <div className={styles.taskHeader}>
          {isEditing ? (
            <input
              type="text"
              value={editName}
              onChange={e => setEditName(e.target.value)}
              onBlur={handleNameSave}
              onKeyDown={e => e.key === 'Enter' && handleNameSave()}
              className={styles.inlineEditInput}
              autoFocus
            />
          ) : (
            <span
              className={styles.taskLabel}
              onClick={() => {
                setEditName(task.label);
                setIsEditing(true);
              }}
              title="Click to edit name"
              style={{ cursor: 'pointer' }}
            >
              {task.label} ✏️
            </span>
          )}
          <span className={styles.taskDuration}>{task.duration}m</span>
        </div>

        {startTime && endTime && (
          <div className={styles.taskTimeRange}>
            {formatTimeDisplay(startTime)} - {formatTimeDisplay(endTime)}
          </div>
        )}

        <div className={styles.durationControl}>
          <button type="button" onClick={() => onDurationChange(index, -5)}>-5</button>
          <button type="button" onClick={() => onDurationChange(index, -1)}>-</button>
          <span>{task.duration}m</span>
          <button type="button" onClick={() => onDurationChange(index, 1)}>+</button>
          <button type="button" onClick={() => onDurationChange(index, 5)}>+5</button>
        </div>

        <div className={styles.taskColorRow}>
          <span className={styles.colorLabel}>Color:</span>
          <div className={styles.taskColorPicker}>
            {availableColors.map(color => (
              <button
                key={color}
                type="button"
                className={`${styles.taskColorBtn} ${taskColor === color ? styles.selected : ''}`}
                style={{ background: color }}
                onClick={() => handleColorSelect(color)}
              />
            ))}
          </div>
        </div>
      </div>

      <button className={styles.deleteBtn} type="button" onClick={() => onDelete(index)}>
        🗑️
      </button>
    </div>
  );
};
